import { z } from 'zod';

// 공통 스키마 버전
export const SCHEMA_VERSION = "1.0.0" as const;

// 이벤트 이름 열거형
export const EVENT_NAMES = [
  // 페이지 공통
  'page_view',
  'page_leave',

  // Exposure (노출)
  'stock_impression',
  'report_impression',
  'strategy_impression',
  'news_rader_impression',

  // Engagement (상호작용)
  'logo_click',
  'nav_home_click',     // 🆕 로고 클릭 (홈 이동)
  'main_tab_click',
  'market_tab_click',
  'stock_click',
  'stock_tab_click',
  'topic_click',        // 🆕 토픽 클릭
  'newsrader_click',    // 🆕 뉴스레이더 클릭
  'list_more_click',    // 🆕 리스트 더보기 클릭
  'section_nav_click',  // 🆕 섹션/배너/카드 클릭
  'modal_open',         // 🆕 모달/바텀시트 열기
  'modal_close',        // 🆕 모달/바텀시트 닫기
  'page_action',        // 🆕 인페이지 액션 (토글/필터/정렬 등)

  // 🔍 검색
  'search_open',        // 🆕 검색 버튼 클릭
  'search_performed',   // 검색 수행
  'report_click',
  'report_like',
  'report_share',
  'strategy_click',
  'strategy_tab_click',
  'strategy_history_nav',
  'strategy_chart_click',
  'watchlist_add_start',
  'watchlist_add_success',
  'watchlist_add_fail',
  'watchlist_remove_start',
  'watchlist_remove_success',
  'watchlist_remove_fail',
  'portfolio_create',
  'portfolio_edit',

  // Intent & Outcome
  'pf_teaser_start',
  'pf_teaser_success',
  'pf_teaser_fail',

  'pf_survey_start',
  'pf_survey_success',
  'pf_survey_fail',


  'unlock_start',
  'unlock_success',
  'unlock_fail',
  'strategy_alarm_start',
  'strategy_alarm_success',
  'strategy_alarm_fail',
  'report_alarm_start',
  'report_alarm_success',
  'report_alarm_fail',
  'stock_alarm_start',
  'stock_alarm_success',
  'stock_alarm_fail',
  'push_permission_start',
  'push_permission_result',
  'push_subscribe_start',
  'push_subscribe_success',
  'push_subscribe_fail',
  // 의도-결과: 리포트 요청
  'report_request_start',
  'report_request_success',
  'report_request_fail',
  // 의도-결과: 리포트 투표
  'vote_click_start',
  'vote_click_success',
  'vote_click_fail',
  // 의도-결과: 리포트 댓글 좋아요
  'report_comment_like_start',
  'report_comment_like_success',
  'report_comment_like_fail',
  // 의도-결과: 리포트 댓글 작성
  'report_comment_start',
  'report_comment_success',
  'report_comment_fail',
  'report_comment_blocked',
  // 의도-결과: 관심종목 등록
  'watchlist_add_start',
  'watchlist_add_success',
  'watchlist_add_fail',
  // 의도-결과: 관심종목 제거
  'watchlist_remove_start',
  'watchlist_remove_success',
  'watchlist_remove_fail',
  // 의도-결과: 종목 알림 신청
  'stock_alarm_start',
  'stock_alarm_success',
  'stock_alarm_fail',
  // 의도-결과: 전략 알림 신청
  'strategy_alarm_start',
  'strategy_alarm_success',
  'strategy_alarm_fail',
  // 의도-결과: 리포트 알림 신청
  'report_alarm_start',
  'report_alarm_success',
  'report_alarm_fail',
  // 의도-결과: 로그인/회원가입
  'auth_start',
  'auth_success',
  'auth_fail',
  // 상호작용: 회원가입 스텝
  'signup_step_view',
  'signup_step_submit',
  // 의도-결과: 포트폴리오 생성 플로우
  'portfolio_flow_start',
  'portfolio_flow_success',
  'portfolio_flow_fail',
  // 상호작용: 포트폴리오 스텝
  'portfolio_step_view',
  'portfolio_step_submit',
  // 의도-결과: 포트폴리오 생성 계산
  'portfolio_create_compute_start',
  'portfolio_create_compute_success',
  'portfolio_create_compute_fail',
  // 의도-결과: 포트폴리오 처방 계산
  'portfolio_diagnosis_compute_start',
  'portfolio_diagnosis_compute_success',
  'portfolio_diagnosis_compute_fail',
  // 의도-결과: 포트폴리오 편집
  'port_edit_start',
  'port_edit_success',
  'port_edit_fail',
  // 의도-결과: 포트폴리오 삭제
  'portfolio_delete_start',
  'portfolio_delete_success',
  'portfolio_delete_fail',
  // 의도-결과: 플래너 생성 플로우
  'planner_flow_start',
  'planner_flow_success',
  'planner_flow_fail',
  // 의도-결과: 플래너 모델 계산
  'planner_compute_start',
  'planner_compute_success',
  'planner_compute_fail',
  // 상호작용: 플래너 스텝
  'planner_step_view',
  'planner_step_submit',
  // 의도-결과: KB 연동 플로우
  'kb_connect_flow_start',
  'kb_connect_flow_success',
  'kb_connect_flow_fail',
  // 의도-결과: KB 연동 스텝
  'kb_connect_step_view',
  'kb_connect_step_submit',

  "kb_order_start",
  "kb_order_success",
  "kb_order_fail",
] as const;

export type EventName = typeof EVENT_NAMES[number];

// 페이지 카테고리
export const PAGE_CATEGORIES = [
  'home',
  'stock_detail',
  'strategy_detail',
  'portfolio',           // 🆕 portfolio_teaser + portfolio_overview 통합
  'report_detail',
  'briefing',            // 🆕 브리핑
  'ranking',             // 🆕 카테고리별 상위 종목
  'search',
  'collection',
  'planner',             // 🆕 플래너
  'kb',                  // 🆕 KB증권
  'auth',
  'settings',
  'tolerance',           // 🆕 투자성향검사
  'static',
  'error'
] as const;

export type PageCategory = typeof PAGE_CATEGORIES[number];

// 엔티티 종류
export const ENTITY_KINDS = ['stock', 'strategy', 'portfolio', 'report', 'none'] as const;
export type EntityKind = typeof ENTITY_KINDS[number];

// Zod 스키마 정의
const ClickIdSchema = z.object({
  gclid: z.string().nullable().optional(),
  gbraid: z.string().nullable().optional(),
  wbraid: z.string().nullable().optional(),
  n_media: z.string().nullable().optional(),
  msclkid: z.string().nullable().optional(),
});

const UTMSchema = z.object({
  source: z.string().nullable().optional(),
  medium: z.string().nullable().optional(),
  campaign: z.string().nullable().optional(),
  term: z.string().nullable().optional(),
  content: z.string().nullable().optional(),
  click_id: ClickIdSchema.optional(),
});

const PageSchema = z.object({
  url: z.string(),
  path: z.string(),
  title: z.string().nullable().optional(),
  referrer: z.string().nullable().optional(),
  ref_domain: z.string().nullable().optional(),
});

const DeviceSchema = z.object({
  ua: z.string(),
  browser: z.string().nullable().optional(),
  browser_version: z.string().nullable().optional(),
  os: z.string().nullable().optional(),
  os_version: z.string().nullable().optional(),
  device_type: z.enum(['desktop', 'mobile', 'tablet', 'bot']).nullable().optional(),
  locale: z.string().nullable().optional(),
  tz: z.string().nullable().optional(),
  screen_w: z.number().nullable().optional(),
  screen_h: z.number().nullable().optional(),
  dpr: z.number().nullable().optional(),
});

const ExpSchema = z.object({
  ab: z.record(z.string(), z.string()).optional(),
}).optional();

const PerfSchema = z.object({
  ttfb_ms: z.number().optional(),
  fcp_ms: z.number().optional(),
  lcp_ms: z.number().optional(),
}).optional();

// 공통 이벤트 스키마
export const CommonEventSchema = z.object({
  schema_version: z.literal(SCHEMA_VERSION),
  event_id: z.string(),
  event_name: z.enum(EVENT_NAMES),
  client_ts: z.number().int().nonnegative(),
  anon_id: z.string(),
  user_id: z.string().nullable(),
  session_id: z.string(),
  view_id: z.string(),  // 🆕 한 화면에 머무른 구간을 나타내는 ID
  page: PageSchema,
  utm: UTMSchema,
  device: DeviceSchema,
  exp: ExpSchema,
  perf: PerfSchema,
  props: z.record(z.string(), z.any()),
});

export type CommonEvent = z.infer<typeof CommonEventSchema>;

// 이벤트별 Props 타입 정의
export interface PageViewProps {
  page_category: PageCategory;
  page_subtype?: string;         // 🆕 페이지 소분류 (예: portfolio 랜딩, 티저, 처방 등)
  route_template?: string;
  entry?: boolean;
  entity?: {
    kind: EntityKind;
    ticker?: string;
    market?: string;
    strategy_id?: string;
    portfolio_id?: string;
    report_id?: string;
  };
  trace_id?: string;
  from_list_id?: string;
  from_section_id?: string;      // 🆕 유입 섹션 ID
  path_params?: Record<string, string>;
  query_slim?: Record<string, string>;
}

export interface PageLeaveProps {
  page_category: PageCategory;
  page_subtype?: string;         // 🆕 페이지 소분류
  route_template?: string;
  stay_ms: number;
  scroll_depth_pct?: number;
  interaction_cnt?: number;
}

// 🆕 배치 형식 노출 이벤트
export interface StockImpressionProps {
  list_id: string;
  items: Array<{
    ticker: string;
    market: string;
    rank_pos: number;
  }>;
  total_count: number;
  visible_above_the_fold: boolean;
}

// 🆕 배치 형식 노출 이벤트
export interface ReportImpressionProps {
  list_id: string;
  items: Array<{
    report_id: string;
    rank_pos: number;
    trace_id?: string;
    topic_cluster?: string;
  }>;
  total_count: number;
  visible_above_the_fold: boolean;
}

// 🆕 뉴스레이더 배치 노출 이벤트
export interface NewsRaderImpressionProps {
  list_id: string;
  items: Array<{
    newsrader_id: string;
    market: string;
    ticker: string;
    rank_pos: number;
  }>;
  total_count: number;
  visible_above_the_fold: boolean;
}

// 🆕 배치 형식 노출 이벤트
export interface StrategyImpressionProps {
  list_id: string;
  items: Array<{
    strategy_id: string;
    market: string;
    rank_pos: number;
  }>;
  total_count: number;
  visible_above_the_fold: boolean;
}

export interface StockClickProps {
  ticker: string;
  market: 'kr' | 'us';
  cat?: 'stock' | 'index' | 'crypto';  // 카테고리
  from_list_id?: string;
  rank_pos?: number;
  trace_id?: string;
  search_intent_id?: string;  // 검색을 통한 클릭인 경우
}

export interface ReportClickProps {
  report_id: number;
  from_list_id?: string;
  rank_pos?: number;
  trace_id?: string;
  search_intent_id?: string;  // 검색을 통한 클릭인 경우
}

export interface NewsRaderClickProps {
  ticker: string;
  newsrader_id: number;
  from_list_id?: string;
  rank_pos?: number;
  trace_id?: string;
}

export interface StrategyClickProps {
  strategy_id: string;
  from_list_id?: string;
  rank_pos?: number;
  trace_id?: string;
}

// 🏠 홈 네비게이션
export interface NavHomeClickProps {
  trace_id?: string;
}

// 🏷️ 토픽 클릭
export interface TopicClickProps {
  topic_id: number;
  from_list_id?: string;
  rank_pos?: number;
  trace_id?: string;
}

// 🔗 리스트 더보기 클릭
export interface ListMoreClickProps {
  list_id: string;        // 리스트/섹션의 고유 ID
  trace_id?: string;      // 이동할 page_view와 연결
}

// 🔗 섹션 네비게이션 클릭
export interface SectionNavClickProps {
  section_id?: string;    // 섹션 ID
  nav_key?: string;       // 네비게이션 키 (배너/카드/버튼 등 ID)
  trace_id?: string;      // 이동할 page_view와 연결
}

// 🪟 모달/바텀시트 상호작용
export interface ModalOpenProps {
  modal_id: string;                     // 모달/바텀시트 템플릿 고유 ID
  modal_instance_id: string;            // 한번의 오픈 인스턴스 식별자 (ULID)
  trigger_section_id?: string;          // 트리거가 속한 섹션 ID
  intent_id?: string;                   // 오픈과 함께 시작되는 퍼널 의도 ID
  trigger_type?: 'user_click' | 'auto'; // 유저 클릭 | 자동 오픈
}

export interface ModalCloseProps {
  modal_id: string;                        // 모달/바텀시트 템플릿 고유 ID
  modal_instance_id: string;               // open과 1:1 연결되는 인스턴스 ID
  stay_ms: number;                         // 체류 시간(ms)
  reason: 'backdrop' | 'cancel' | 'success'; // 닫힌 이유
}

// 🎯 페이지 액션 (토글/필터/정렬 등)
export interface PageActionProps {
  action_group: 'toggle' | 'sort' | 'filter' | 'range' | 'expand' | 'collapse' | 'copy' | 'share' | 'export' | 'select' | 'input' | 'slider' | 'move' | 'check' | 'flip' | 'page_action' | 'more'
  action_key: string;  // 구체적인 액션 ID (예: filter_sector, toggle_won, sort_watchlist)
}

// 📄 리포트 상호작용
export interface ReportLikeProps {
  report_id: number;
  is_like: boolean;  // true=좋아요, false=해제
}

export interface ReportShareProps {
  report_id: number;
}

// 🔍 검색 관련
export interface SearchOpenProps {
  search_type: 'stock' | 'unified';  // 종목 전용 | 통합검색
  source: string;                     // main_header | feed_stock | stock_detail | report_detail 
  intent_id?: string;                 // 모달 열릴 경우 search_performed와 연결
}

export interface SearchPerformedProps {
  query: string;                      // 검색어 (가장 중요!)
  search_type: 'stock' | 'unified' | 'watchlist' | 'portfolio';
  result_count: number;               // 검색 결과 개수
  render_seq: number;                 // 같은 intent 내 렌더링 순번 (1~)
  latency_ms?: number;                // 검색 결과 지연시간(ms) (optional)
}

// 탭 클릭 공통 Props
export interface TabClickProps {
  to_tab: string;                     // 이동 대상 탭
  to_tab_index: number;               // 이동 대상 탭 순번
  from_tab: string;                   // 직전 활성 탭
  from_tab_index: number;             // 직전 활성 탭 순번
  dwell_prev_ms: number;              // 직전 탭 체류시간
  repeat_click: boolean;              // to==from (상태 변화 없는 클릭)
  trigger: 'tab' | 'shortcut';        // 탭 직접 클릭 | 쇼트컷
  source_key?: string;                // shortcut일 때 트리거 위치
}

// 마켓 탭 클릭 Props
export interface MarketTabClickProps {
  market: 'kr' | 'us' | 'crypto';     // 선택한 마켓
  dwell_prev_ms: number;              // 직전 마켓 체류시간
}

export interface PfTeaserStartProps {
  intent_id: string;
  location?: string;
  paywall_type_pred?: 'login' | 'signup' | 'payment' | 'unknown';
}


export interface PfTeaserOutcomeProps {
  intent_id: string;
  paywall_type: 'login' | 'signup' | 'payment' | 'unknown';
  fail_reason?: 'client_cancel' | 'validation_error' | 'backend_4xx' | 'backend_5xx' | 'timeout' | 'permission_denied' | 'abandon';

}


export interface PfSurveyStartProps {
  intent_id: string;
  location?: string;
  paywall_type_pred?: 'login' | 'signup' | 'payment' | 'unknown';
}


export interface PfSurveyOutcomeProps {
  intent_id: string;
  paywall_type: 'login' | 'signup' | 'payment' | 'unknown';
  fail_reason?: 'client_cancel' | 'validation_error' | 'backend_4xx' | 'backend_5xx' | 'timeout' | 'permission_denied' | 'abandon';
}


export interface UnlockStartProps {
  intent_id: string;
  location?: string;
  paywall_type_pred?: 'login' | 'signup' | 'payment' | 'unknown';
}

export interface UnlockOutcomeProps {
  intent_id: string;
  paywall_type: 'login' | 'signup' | 'payment' | 'unknown';
  fail_reason?: 'client_cancel' | 'validation_error' | 'backend_4xx' | 'backend_5xx' | 'timeout' | 'permission_denied' | 'abandon';
}

// Auth (로그인/회원가입) 의도-결과
export interface AuthStartProps {
  intent_id: string;
  ui: 'bottom_sheet' | 'page';
  entry_reason: 'gate' | 'direct';
  method_kind: 'oauth' | 'password';
  mode_pred: 'login' | 'signup' | 'unknown';
  provider: 'kakao' | 'google' | null;
  source_key: string;
  return_to?: string;
  parent_intent_id?: string;
  required_gate?: 'login' | 'none';
}

export interface AuthOutcomeProps {
  intent_id: string;
  // method_kind: 'oauth' | 'password';
  // provider: 'kakao' | 'google' | null;
  // mode_resolved: 'login' | 'signup';
  latency_ms?: number;
  fail_reason?: string;
}

// 회원가입 스텝 이벤트
export interface SignupStepViewProps {
  intent_id: string;
  step_key: 'credential_verify' | 'nickname_set' | 'phone_verify' | 'tos_consent' | 'asset_profile';
  step_index: number;
}

export interface SignupStepSubmitProps {
  intent_id: string;
  step_key: 'credential_verify' | 'nickname_set' | 'phone_verify' | 'tos_consent' | 'asset_profile';
  step_index: number;
  outcome: 'ok' | 'fail';
  fail_reason?: string;
}

// 포트폴리오 생성 플로우 (의도-결과)
export type PortfolioStepKey =
  | 'search_select'       // 종목 검색/선택
  | 'qty_avg_choice'      // 수량/평단 입력 여부 선택
  | 'qty_avg_input'       // 수량/평단 입력 폼 제출
  | 'teaser_score'        // 스코어 티저 확인
  | 'teaser_sim'         // 상관관계 티저 확인
  | 'teaser_risk'         // 리스크 티저 확인
  | 'teaser_diag'         // 종합 진단
  | 'teaser_diag_choice'  // 처방 진행 클릭
  | 'survey_landing'      // 설문 랜딩
  | 'survey_step1'        // 설문 1
  | 'survey_step2'        // 설문 2
  | 'survey_step3'        // 설문 3
  | 'survey_ready'        // 설문 준비
  | 'login_gate'          // 로그인 모달
  | 'diagnosis';          // 최종 처방/언락 렌더

export interface PortfolioFlowStartProps {
  intent_id: string;
  parent_intent_id?: string;
  required_gate?: 'login' | 'none';
}

export interface PortfolioFlowOutcomeProps {
  intent_id: string;
  portfolio_id?: number;
  latency_ms?: number;
  fail_reason?: string;
}

// 포트폴리오 스텝 이벤트
export interface PortfolioStepViewProps {
  intent_id: string | null;
  step_key: PortfolioStepKey;
  step_index: number;
  portfolio_id?: string | number;  // 생성 이후, 비회원일 경우 hash 값
}

export interface PortfolioStepSubmitProps {
  intent_id: string | null;
  step_key: PortfolioStepKey;
  step_index: number;
  sub_step_index?: number;  // 하위 스텝 순서 (수량/평단 입력 단계)
  outcome: 'ok' | 'fail';
  fail_reason?: string;
  choice?: 'input' | 'skip' | 'proceed';  // input: 수량/평단 입력 선택O, skip: 선택X, proceed: 처방 진행 선택O
  submit_mode?: 'client_only' | 'server_request';  // client_only 또는 server_request(모델 계산)
  tickers?: string[];  // search_select 시
  weights?: number[];  // qty_avg_input 시
  answer_key?: string;  // survey_step1,2,3 시
}

// 포트폴리오 생성 계산 (의도-결과)
export interface PortfolioCreateComputeStartProps {
  intent_id: string;
  portfolio_id: number;
  parent_intent_id?: string;
  required_gate?: 'login' | 'none';
  tickers?: string[];
}

export interface PortfolioCreateComputeOutcomeProps {
  intent_id: string;
  portfolio_id: number;
  latency_ms?: number;
  fail_reason?: string;
}

// 포트폴리오 처방 계산 (의도-결과)
export interface PortfolioDiagnosisComputeStartProps {
  intent_id: string;
  portfolio_id: number;
  parent_intent_id?: string;
  required_gate?: 'login' | 'none';
}

export interface PortfolioDiagnosisComputeOutcomeProps {
  intent_id: string;
  portfolio_id: number;
  latency_ms?: number;
  fail_reason?: string;
}

// 포트폴리오 편집 (의도-결과)
export interface PortfolioItem {
  ticker: string;
  quantity: number;
}

export interface PortEditStartProps {
  intent_id: string;
  portfolio_id: number;
  prev_items: PortfolioItem[];
  parent_intent_id?: string;
  required_gate?: 'login' | 'none';
}

export interface PortEditSuccessProps {
  intent_id: string;
  portfolio_id: number;
  cur_items: PortfolioItem[];
  latency_ms?: number;
}

export interface PortEditFailProps {
  intent_id: string;
  portfolio_id: number;
  fail_reason?: string;
}

// 포트폴리오 삭제 (의도-결과)
export interface PortfolioDeleteStartProps {
  intent_id: string;
  portfolio_id: number;
  from_list_id?: string;
  parent_intent_id?: string;
  required_gate?: 'login' | 'none';
}

export interface PortfolioDeleteOutcomeProps {
  intent_id: string;
  portfolio_id: number;
  latency_ms?: number;
  fail_reason?: string;
}

// 플래너 생성 플로우 (의도-결과)
export interface PlannerFlowStartProps {
  intent_id: string;
  parent_intent_id?: string;
  required_gate?: 'login' | 'none';
}

export interface PlannerFlowOutcomeProps {
  intent_id: string;
  planner_id?: number;
  latency_ms?: number;
  fail_reason?: string;
}

// 플래너 모델 계산 (의도-결과)
export interface PlannerComputeStartProps {
  intent_id: string;
  parent_intent_id?: string;
  required_gate?: 'login' | 'none';
  answer?: any;
}

export interface PlannerComputeOutcomeProps {
  intent_id: string;
  portfolio_id?: number;
  latency_ms?: number;
  fail_reason?: string;
}

// 플래너 스텝 이벤트
export type PlannerStepKey =
  | 'q_spend_category'        // 소비 카테고리
  | 'q_spend_amount'          // 소비 금액
  | 'q_spend_timing'          // 소비 시점
  | 'q_savings_accumulated'   // 지금까지 모은 돈
  | 'q_savings_monthly'       // 월 저축액
  | 'result_fail'             // 결과 실패
  | 'result_main'             // 결과 화면
  | 'result_notacieve'        // 결과 미달성
  | 'plan_this_month_modal'   // 이번달 종목 화면
  | 'planner_compute'         // 계산 트리거
  | 'apply_suggested_goal';   // 재시도

export interface PlannerStepViewProps {
  intent_id: string;
  step_key: PlannerStepKey;
  step_index: number;
}

export interface PlannerStepSubmitProps {
  intent_id: string;
  step_key: PlannerStepKey;
  step_index: number;
  outcome?: 'ok' | 'fail';
  fail_reason?: string;
}

// 리포트 요청 (의도-결과)
export interface ReportRequestStartProps {
  intent_id: string;
  ticker: string;
  market: 'kr' | 'us';
  parent_intent_id?: string;
  required_gate?: 'login' | 'none';
}

export interface ReportRequestOutcomeProps {
  intent_id: string;
  ticker: string;
  market: 'kr' | 'us';
  latency_ms?: number;
  fail_reason?: string;
}

// 리포트 투표 (의도-결과)
export interface VoteClickStartProps {
  intent_id: string;
  report_id: number;
  option: 'o' | 'x' | 'a' | 'b' | 'c' | 'd';
  parent_intent_id?: string;
  required_gate?: 'login' | 'none';
}

export interface VoteClickOutcomeProps {
  intent_id: string;
  report_id: number;
  option: 'o' | 'x' | 'a' | 'b' | 'c' | 'd';
  latency_ms?: number;
  fail_reason?: string;
}

// 리포트 댓글 좋아요 (의도-결과)
export interface ReportCommentLikeStartProps {
  intent_id: string;
  report_id: number;
  comment_id: number;
  is_like: boolean;
  parent_intent_id?: string;
  required_gate?: 'login' | 'none';
}

export interface ReportCommentLikeOutcomeProps {
  intent_id: string;
  report_id: number;
  comment_id: number;
  is_like: boolean;
  latency_ms?: number;
  fail_reason?: string;
}

// 리포트 댓글 작성 (의도-결과)
export interface ReportCommentStartProps {
  intent_id: string;
  report_id: number;
  parent_comment_id?: number;
  parent_intent_id?: string;
  required_gate?: 'login' | 'none';
}

export interface ReportCommentOutcomeProps {
  intent_id: string;
  report_id: number;
  parent_comment_id?: number;
  latency_ms?: number;
  fail_reason?: string;
}

export interface ReportCommentBlockedProps {
  intent_id: string;
  report_id: number;
  parent_comment_id?: number;
  block_reason?: string;
}

// 관심종목 등록 (의도-결과)
export interface WatchlistAddStartProps {
  intent_id: string;
  ticker: string | string[];
  market?: 'kr' | 'us' | ('kr' | 'us')[];  // 단일 또는 배열 (ticker와 1:1 매칭)
  cat?: 'stock' | 'index' | 'crypto';
  from_list_id: string;
  rank_pos?: number;
  parent_intent_id?: string;
  required_gate?: 'login' | 'none';
}

export interface WatchlistAddOutcomeProps {
  intent_id: string;
  ticker: string | string[];
  market?: 'kr' | 'us' | ('kr' | 'us')[];  // 단일 또는 배열 (ticker와 1:1 매칭)
  cat?: 'stock' | 'index' | 'crypto';
  from_list_id: string;
  rank_pos?: number;
  latency_ms?: number;
  fail_reason?: string;
}

// 관심종목 제거 (의도-결과)
export interface WatchlistRemoveStartProps {
  intent_id: string;
  ticker: string | string[];
  market?: 'kr' | 'us' | ('kr' | 'us')[];  // 단일 또는 배열 (ticker와 1:1 매칭)
  cat?: 'stock' | 'index' | 'crypto';
  from_list_id: string;
  rank_pos?: number;
  parent_intent_id?: string;
  required_gate?: 'login' | 'none';
}

export interface WatchlistRemoveOutcomeProps {
  intent_id: string;
  ticker: string | string[];
  market?: 'kr' | 'us' | ('kr' | 'us')[];  // 단일 또는 배열 (ticker와 1:1 매칭)
  cat?: 'stock' | 'index' | 'crypto';
  from_list_id: string;
  rank_pos?: number;
  latency_ms?: number;
  fail_reason?: string;
}

// 종목 알림 신청 (의도-결과)
export interface StockAlarmStartProps {
  intent_id: string;
  ticker: string;
  market: 'kr' | 'us';
  from_section_id: string;
  parent_intent_id?: string;
  required_gate?: 'login' | 'none';
}

export interface StockAlarmOutcomeProps {
  intent_id: string;
  ticker: string;
  market: 'kr' | 'us';
  from_section_id: string;
  latency_ms?: number;
  fail_reason?: string;
}

// 전략 알림 신청 (의도-결과)
export interface StrategyAlarmStartProps {
  intent_id: string;
  strategy_id: string;
  market: 'kr' | 'us';
  from_section_id: string;
  parent_intent_id?: string;
  required_gate?: 'login' | 'none';
}

export interface StrategyAlarmOutcomeProps {
  intent_id: string;
  strategy_id: string;
  market: 'kr' | 'us';
  from_section_id: string;
  latency_ms?: number;
  fail_reason?: string;
}

// 리포트 알림 신청 (의도-결과)
export interface ReportAlarmStartProps {
  intent_id: string;
  report_id?: number;
  from_list_id: string;
  parent_intent_id?: string;
  required_gate?: 'login' | 'none';
}

export interface ReportAlarmOutcomeProps {
  intent_id: string;
  report_id?: number;
  from_list_id: string;
  latency_ms?: number;
  fail_reason?: string;
}

export interface KBConnectFlowStartProps {
  intent_id: string;
  parent_intent_id?: string;
  required_gate?: 'login' | 'none';
}

export interface KBConnectFlowOutcomeProps {
  intent_id: string;
  latency_ms?: number;
  fail_reason?: string;
}

export type KBConnectStepKey =
  | 'connect_type_select'  // 연동 유형 선택
  | 'verify_identity' // 본인인증
  | 'move_kb'
  | 'terms'
  | 'create_account'
  | 'no_exist_account'
  | 'exist_account'
  | 'cross_site_unlock'
  | 'connect_success'

// 의도-결과: KB 연동 스텝
export interface KBConnectStepViewProps {
  intent_id: string | null;
  step_key: KBConnectStepKey;
}

export interface KBConnectStepSubmitProps {
  intent_id: string | null;
  step_key: KBConnectStepKey;
  sub_step_index?: number;
  outcome: 'ok' | 'fail';
  fail_reason?: string;
}

export interface KBConnectStepSubmitOutcomeProps {
  intent_id: string;
  step_key: KBConnectStepKey;
  sub_step_index?: number;
  outcome: 'ok' | 'fail';
  fail_reason?: string;
}

export interface KBOrderStartProps {
  intent_id: string;
  order_type: 'buy' | 'sell';
  ticker: string;
}

export interface KBOrderOutcomeProps {
  intent_id: string;
  latency_ms?: number;
  fail_reason?: string;
}

// 이벤트별 Props 매핑
export interface EventPropsMap {

  page_view: PageViewProps;
  page_leave: PageLeaveProps;
  stock_impression: StockImpressionProps;
  report_impression: ReportImpressionProps;
  strategy_impression: StrategyImpressionProps;
  news_rader_impression: NewsRaderImpressionProps;
  stock_click: StockClickProps;
  report_click: ReportClickProps;
  strategy_click: StrategyClickProps;
  topic_click: TopicClickProps;
  newsrader_click: NewsRaderClickProps;
  list_more_click: ListMoreClickProps;
  section_nav_click: SectionNavClickProps;
  modal_open: ModalOpenProps;
  modal_close: ModalCloseProps;
  page_action: PageActionProps;
  nav_home_click: NavHomeClickProps;

  // 🔍 검색
  search_open: SearchOpenProps;
  search_performed: SearchPerformedProps;

  pf_teaser_start: PfTeaserStartProps;
  pf_teaser_success: PfTeaserOutcomeProps;
  pf_teaser_fail: PfTeaserOutcomeProps;
  pf_survey_start: PfSurveyStartProps;
  pf_survey_success: PfSurveyOutcomeProps;
  pf_survey_fail: PfSurveyOutcomeProps;
  unlock_start: UnlockStartProps;
  unlock_success: UnlockOutcomeProps;
  unlock_fail: UnlockOutcomeProps;
  auth_start: AuthStartProps;
  auth_success: AuthOutcomeProps;
  auth_fail: AuthOutcomeProps;
  signup_step_view: SignupStepViewProps;
  signup_step_submit: SignupStepSubmitProps;
  portfolio_flow_start: PortfolioFlowStartProps;
  portfolio_flow_success: PortfolioFlowOutcomeProps;
  portfolio_flow_fail: PortfolioFlowOutcomeProps;
  portfolio_step_view: PortfolioStepViewProps;
  portfolio_step_submit: PortfolioStepSubmitProps;
  portfolio_create_compute_start: PortfolioCreateComputeStartProps;
  portfolio_create_compute_success: PortfolioCreateComputeOutcomeProps;
  portfolio_create_compute_fail: PortfolioCreateComputeOutcomeProps;
  portfolio_diagnosis_compute_start: PortfolioDiagnosisComputeStartProps;
  portfolio_diagnosis_compute_success: PortfolioDiagnosisComputeOutcomeProps;
  portfolio_diagnosis_compute_fail: PortfolioDiagnosisComputeOutcomeProps;
  port_edit_start: PortEditStartProps;
  port_edit_success: PortEditSuccessProps;
  port_edit_fail: PortEditFailProps;
  portfolio_delete_start: PortfolioDeleteStartProps;
  portfolio_delete_success: PortfolioDeleteOutcomeProps;
  portfolio_delete_fail: PortfolioDeleteOutcomeProps;
  planner_flow_start: PlannerFlowStartProps;
  planner_flow_success: PlannerFlowOutcomeProps;
  planner_flow_fail: PlannerFlowOutcomeProps;
  planner_compute_start: PlannerComputeStartProps;
  planner_compute_success: PlannerComputeOutcomeProps;
  planner_compute_fail: PlannerComputeOutcomeProps;
  planner_step_view: PlannerStepViewProps;
  planner_step_submit: PlannerStepSubmitProps;
  report_alarm_start: ReportAlarmStartProps;
  report_alarm_success: ReportAlarmOutcomeProps;
  report_alarm_fail: ReportAlarmOutcomeProps;
  stock_alarm_start: StockAlarmStartProps;
  stock_alarm_success: StockAlarmOutcomeProps;
  stock_alarm_fail: StockAlarmOutcomeProps;
  strategy_alarm_start: StrategyAlarmStartProps;
  strategy_alarm_success: StrategyAlarmOutcomeProps;
  strategy_alarm_fail: StrategyAlarmOutcomeProps;
  report_request_start: ReportRequestStartProps;
  report_request_success: ReportRequestOutcomeProps;
  report_request_fail: ReportRequestOutcomeProps;
  vote_click_start: VoteClickStartProps;
  vote_click_success: VoteClickOutcomeProps;
  vote_click_fail: VoteClickOutcomeProps;
  report_comment_like_start: ReportCommentLikeStartProps;
  report_comment_like_success: ReportCommentLikeOutcomeProps;
  report_comment_like_fail: ReportCommentLikeOutcomeProps;
  report_comment_start: ReportCommentStartProps;
  report_comment_success: ReportCommentOutcomeProps;
  report_comment_fail: ReportCommentOutcomeProps;
  report_comment_blocked: ReportCommentBlockedProps;
  watchlist_add_start: WatchlistAddStartProps;
  watchlist_add_success: WatchlistAddOutcomeProps;
  watchlist_add_fail: WatchlistAddOutcomeProps;
  watchlist_remove_start: WatchlistRemoveStartProps;
  watchlist_remove_success: WatchlistRemoveOutcomeProps;
  watchlist_remove_fail: WatchlistRemoveOutcomeProps;
  // 기본 props가 없는 이벤트들
  logo_click: Record<string, any>;
  main_tab_click: TabClickProps;
  market_tab_click: MarketTabClickProps;
  stock_tab_click: TabClickProps;
  strategy_tab_click: TabClickProps;
  report_like: ReportLikeProps;
  report_share: ReportShareProps;
  strategy_history_nav: Record<string, any>;
  strategy_chart_click: Record<string, any>;
  portfolio_create: Record<string, any>;
  portfolio_edit: Record<string, any>;
  push_permission_start: Record<string, any>;
  push_permission_result: Record<string, any>;
  push_subscribe_start: Record<string, any>;
  push_subscribe_success: Record<string, any>;
  push_subscribe_fail: Record<string, any>;
  kb_connect_flow_start: KBConnectFlowStartProps;
  kb_connect_flow_success: KBConnectFlowOutcomeProps;
  kb_connect_flow_fail: KBConnectFlowOutcomeProps;
  kb_connect_step_view: KBConnectStepViewProps;
  kb_connect_step_submit: KBConnectStepSubmitProps;

  kb_order_start: KBOrderStartProps;
  kb_order_success: KBOrderOutcomeProps;
  kb_order_fail: KBOrderOutcomeProps;
}

// 배치 전송 스키마
export const BatchEventSchema = z.array(CommonEventSchema);
export type BatchEvent = z.infer<typeof BatchEventSchema>;

// 스키마 검증 함수
export function validateEvent(event: CommonEvent, debug = false): boolean {
  try {
    CommonEventSchema.parse(event);
    return true;
  } catch (error) {
    if (debug) {
      console.warn('Analytics schema validation failed:', error);
      console.warn('Event data:', JSON.stringify(event, null, 2));
    }
    return false;
  }
}

// 이벤트 생성 헬퍼
export function createEvent<T extends EventName>(
  eventName: T,
  props: EventPropsMap[T],
  commonFields: Omit<CommonEvent, 'event_name' | 'props'>
): CommonEvent {
  return {
    ...commonFields,
    event_name: eventName,
    props: props as Record<string, any>,
  };
}
